import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';

import { catchError, tap, throwError } from 'rxjs';

import { ConditionalWriteRecoveryService } from '@common/service/conditional-write-recovery.service';
import {
  CORE_CAPABILITY_HEADER,
  CORE_ETAG_HEADER,
  CORE_IF_MATCH_HEADER,
  RecordRevisionStore,
} from '@common/service/record-revision.store';

/** Only these methods can be guarded. POST is excluded on purpose: legacy collection create AND
 * upsert are unguarded today, legacy create stays unguarded forever (nothing to be stale
 * against), and a legacy ARRAY body carrying If-Match is a 400. */
const GUARDABLE_METHODS = new Set(['PUT', 'PATCH', 'DELETE']);

/**
 * AK-720 — AK-673 conditional writes.
 *
 * Registered LAST in app.config so that on the request leg it sees the URL after
 * requestTransformerInterceptor has rewritten it to the absolute core endpoint (and after auth
 * headers are attached), and on the response leg it sees errors FIRST.
 */
export const conditionalWriteInterceptor: HttpInterceptorFn = (req, next) => {
  const store = inject(RecordRevisionStore);
  const recovery = inject(ConditionalWriteRecoveryService);

  const capture = (event: HttpEvent<unknown>): void => {
    if (!(event instanceof HttpResponse)) return;

    const classKey = store.parseCoreClassKey(req.url);
    if (classKey) {
      const capability = event.headers.get(CORE_CAPABILITY_HEADER);
      if (capability) store.setCapability(classKey, capability);
    }

    const record = store.parseCoreRecordPath(req.url);
    if (!record) return;

    // A successful DELETE leaves no successor revision — drop the tag rather than keep a
    // validator for a row that no longer exists.
    if (req.method.toUpperCase() === 'DELETE') {
      store.clearTag(record.recordKey);
      return;
    }

    // The write response's ETag is authoritative: rotating from it is what removes the need
    // for a follow-up GET. A response with no ETag is normal (inherited row, un-stamped row,
    // multi-row list) and must leave the stored tag alone.
    const tag = event.headers.get(CORE_ETAG_HEADER);
    if (tag) store.setTag(record.recordKey, tag);
  };

  const guarded = applyIfMatch(req, store);

  return next(guarded).pipe(
    tap({ next: capture }),
    catchError((error) => {
      if (!(error instanceof HttpErrorResponse)) return throwError(() => error);

      const record = store.parseCoreRecordPath(req.url);
      if (!record || !GUARDABLE_METHODS.has(req.method.toUpperCase())) return throwError(() => error);

      const recovered =
        recovery.recoverFromConflict(guarded, error, next, record) ??
        recovery.recoverFromDefect(guarded, error, next, record);
      return recovered ?? throwError(() => error);
    }),
  );
};

/**
 * Attaches the stored validator to a guarded write. Returns the request unchanged whenever the
 * write is not guardable — an absent If-Match is always a legal request (the server either
 * accepts it, because the row carries no revision yet, or answers 428, which the recovery
 * service turns into a re-read).
 */
function applyIfMatch(req: HttpRequest<unknown>, store: RecordRevisionStore): HttpRequest<unknown> {
  if (!GUARDABLE_METHODS.has(req.method.toUpperCase())) return req;
  if (req.headers.has(CORE_IF_MATCH_HEADER)) return req;

  const record = store.parseCoreRecordPath(req.url);
  if (!record) return req;

  // The server 400s a validator supplied for an ineligible class.
  if (store.getCapability(record.classKey) === 'class_not_eligible') return req;

  // AK-723: method-aware. A validator seeded from a collection row may be PATCH-scoped
  // server-side (a `show_list` projection), so `getTagForMethod` withholds it from a PUT/DELETE
  // — those keep AK-720's behaviour of going out bare and self-repairing on 428.
  const tag = store.getTagForMethod(record.recordKey, req.method);
  if (!tag) return req;

  return req.clone({ headers: req.headers.set(CORE_IF_MATCH_HEADER, tag) });
}
